import React, { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    Chip,
    Switch,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Autocomplete,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ContactMail as ContactMailIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import type {
    ExterniKontakt,
    ExterniKontaktRequest,
    ExterniKontaktTyp,
} from '../../api/JsdhNotifikaceService/generated/models/ExterniKontakt';
import { KONTAKT_TYP_LABELS } from '../../api/JsdhNotifikaceService/generated/models/ExterniKontakt';
import {
    useExterniKontaktyQuery,
    useCreateExterniKontaktMutation,
    useUpdateExterniKontaktMutation,
    useDeleteExterniKontaktMutation,
} from '../../api/JsdhNotifikaceService/service/ExterniKontaktyMutations';
import { useAllUsersQuery } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import type { Uzivatel } from '../../api/JsdhNotifikaceService/generated/models/Uzivatel';

const EMPTY_FORM: ExterniKontaktRequest = {
    typ: 'STAROSTA',
    jmeno: '',
    email: '',
    telefon: '',
    clenId: null,
    posilatSouhrn: false,
    aktivni: true,
};

const ExterniKontaktyPage: React.FC = () => {
    const { data: kontakty, isLoading, error } = useExterniKontaktyQuery();
    const { data: uzivatele } = useAllUsersQuery();
    const createMutation = useCreateExterniKontaktMutation();
    const updateMutation = useUpdateExterniKontaktMutation();
    const deleteMutation = useDeleteExterniKontaktMutation();

    const [openDialog, setOpenDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selected, setSelected] = useState<ExterniKontakt | null>(null);
    const [formData, setFormData] = useState<ExterniKontaktRequest>(EMPTY_FORM);

    const handleOpenCreate = () => {
        setSelected(null);
        setFormData(EMPTY_FORM);
        setOpenDialog(true);
    };

    const handleOpenEdit = (k: ExterniKontakt) => {
        setSelected(k);
        setFormData({
            typ: k.typ,
            jmeno: k.jmeno,
            email: k.email || '',
            telefon: k.telefon || '',
            clenId: k.clenId ?? null,
            posilatSouhrn: k.posilatSouhrn,
            aktivni: k.aktivni,
        });
        setOpenDialog(true);
    };

    const handleOpenDelete = (k: ExterniKontakt) => {
        setSelected(k);
        setOpenDeleteDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelected(null);
    };

    const handleSubmit = () => {
        const payload: ExterniKontaktRequest = {
            ...formData,
            clenId: formData.typ === 'RODINA' ? (formData.clenId ?? null) : null,
            posilatSouhrn: formData.typ !== 'RODINA' ? formData.posilatSouhrn : false,
        };
        if (selected) {
            updateMutation.mutate(
                { id: selected.id, data: payload },
                { onSuccess: handleCloseDialog }
            );
        } else {
            createMutation.mutate(payload, { onSuccess: handleCloseDialog });
        }
    };

    const handleDelete = () => {
        if (selected) {
            deleteMutation.mutate(selected.id, {
                onSuccess: () => {
                    setOpenDeleteDialog(false);
                    setSelected(null);
                },
            });
        }
    };

    const isPending = createMutation.isPending || updateMutation.isPending;
    const isFormValid = formData.jmeno.trim() !== '' && formData.email.trim() !== '';

    const selectedClen = uzivatele?.find(u => u.id === formData.clenId) ?? null;

    // Nabízíme jen aktivní členy; aktuálně vybraného (např. mezitím deaktivovaného) ponecháme, aby edit nepadal
    const clenOptions = (uzivatele || []).filter(u => u.aktivni || u.id === formData.clenId);

    if (error) {
        return <Alert severity="error">Chyba při načítání externích kontaktů: {error.message}</Alert>;
    }

    return (
        <Box>
            <PageHeader
                title="Externí kontakty"
                action={
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
                        Přidat kontakt
                    </Button>
                }
            />

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : !kontakty || kontakty.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <ContactMailIcon sx={{ fontSize: 60, color: 'grey.400', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        Žádné externí kontakty
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Žádné externí kontakty — přidejte starostu nebo rodinu člena.
                    </Typography>
                </Paper>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Typ</TableCell>
                                <TableCell>Jméno</TableCell>
                                <TableCell>E-mail</TableCell>
                                <TableCell>Člen</TableCell>
                                <TableCell>Souhrn</TableCell>
                                <TableCell>Aktivní</TableCell>
                                <TableCell align="right">Akce</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {kontakty.map((k) => (
                                <TableRow key={k.id} hover>
                                    <TableCell>
                                        <Chip
                                            label={KONTAKT_TYP_LABELS[k.typ]}
                                            size="small"
                                            color={k.typ === 'STAROSTA' ? 'primary' : k.typ === 'RODINA' ? 'secondary' : 'default'}
                                        />
                                    </TableCell>
                                    <TableCell>{k.jmeno}</TableCell>
                                    <TableCell>{k.email || '—'}</TableCell>
                                    <TableCell>
                                        {k.typ === 'RODINA' ? (k.clenJmeno || '—') : '—'}
                                    </TableCell>
                                    <TableCell>
                                        {k.typ !== 'RODINA' ? (k.posilatSouhrn ? '✓' : '—') : '—'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={k.aktivni ? 'Aktivní' : 'Neaktivní'}
                                            color={k.aktivni ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Upravit">
                                            <IconButton
                                                size="small"
                                                aria-label="Upravit"
                                                onClick={() => handleOpenEdit(k)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Smazat">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                aria-label="Smazat"
                                                onClick={() => handleOpenDelete(k)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {selected ? 'Upravit kontakt' : 'Nový kontakt'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Typ</InputLabel>
                            <Select
                                value={formData.typ}
                                label="Typ"
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        typ: e.target.value as ExterniKontaktTyp,
                                        clenId: null,
                                        posilatSouhrn: false,
                                    })
                                }
                            >
                                {(Object.keys(KONTAKT_TYP_LABELS) as ExterniKontaktTyp[]).map((typ) => (
                                    <MenuItem key={typ} value={typ}>
                                        {KONTAKT_TYP_LABELS[typ]}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label="Jméno"
                            value={formData.jmeno}
                            onChange={(e) => setFormData({ ...formData, jmeno: e.target.value })}
                            required
                            fullWidth
                        />

                        <TextField
                            label="E-mail"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            fullWidth
                            helperText="Jediný kanál — povinné"
                        />

                        {/* Telefon je v modelu jako rezerva pro budouci SMS, ve formulari se zatim nezobrazuje */}

                        {formData.typ === 'RODINA' && (
                            <Autocomplete
                                options={clenOptions}
                                getOptionLabel={(u: Uzivatel) => u.jmeno}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                value={selectedClen}
                                onChange={(_e, val) =>
                                    setFormData({ ...formData, clenId: val?.id ?? null })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Člen jednotky"
                                        inputProps={{ ...params.inputProps, 'aria-label': 'Člen jednotky' }}
                                    />
                                )}
                            />
                        )}

                        {formData.typ !== 'RODINA' && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.posilatSouhrn}
                                            onChange={(e) =>
                                                setFormData({ ...formData, posilatSouhrn: e.target.checked })
                                            }
                                        />
                                    }
                                    label="Uvést, kdo potvrdil účast"
                                />
                                <Typography variant="caption" color="text.secondary" display="block">
                                    Zprávu o výjezdu dostane každý aktivní kontakt 15 minut po vyhlášení
                                    poplachu. Tímto přepínačem se do ní přidá i seznam členů, kteří
                                    potvrdili účast.
                                </Typography>
                            </Box>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.aktivni}
                                    onChange={(e) =>
                                        setFormData({ ...formData, aktivni: e.target.checked })
                                    }
                                />
                            }
                            label="Aktivní"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Zrušit</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isPending || !isFormValid}
                    >
                        {isPending ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                        {selected ? 'Uložit' : 'Vytvořit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Smazat kontakt</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu chcete smazat kontakt <strong>{selected?.jmeno}</strong>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)}>Zrušit</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleteMutation.isPending}
                    >
                        Smazat
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ExterniKontaktyPage;
